import type { Model, Entity, Property } from '../../design/types';
import type { Component } from '../../design/components';
import type { Runner, ReactiveEntity } from '../../design/services';

/** Mutable state container for a single sustain() call. Captured by setter closures. */
interface SustainState {
  reactiveEntities: ReactiveEntity[];
  callback: (entities: ReactiveEntity[]) => void;
}

/**
 * Reactor service - Creates and manages reactive entity state.
 *
 * Each sustain() call creates its own independent state — multiple calls
 * don't interfere with each other.
 */
export class Reactor {
  private runner: Runner;

  constructor(runner: Runner) {
    this.runner = runner;
  }

  sustain(
    model: Model,
    entities: (Entity | null)[],
    callback: (entities: ReactiveEntity[]) => void
  ): void {
    // Each sustain() call gets its own isolated state
    const state: SustainState = { reactiveEntities: [], callback };

    state.reactiveEntities = entities.map((entityData, index) =>
      this.createReactiveEntity(model, entityData, index, state)
    );
    callback(state.reactiveEntities);
  }

  private createReactiveEntity(
    model: Model,
    entityData: Entity | null,
    index: number,
    state: SustainState,
    visitedModels?: Set<string>
  ): ReactiveEntity {
    const visited = visitedModels ? new Set(visitedModels) : new Set<string>();
    if (model.id) visited.add(model.id);

    // Build model with synthetic properties
    const augmentedModel = { ...model, properties: [...model.properties] };

    // Inject entity id so this.id works in Code strings (id is always a primary key, never a model property)
    if (entityData?.id && !model.properties.some(p => p.name === 'id')) {
      augmentedModel.properties.push({ name: 'id', type: 'string' } as Property);
    }

    // Inject __label__ synthetic for model label
    if (model.label && !model.properties.some(p => p.name === '__label__')) {
      augmentedModel.properties.push({ name: '__label__', type: 'string', computed: model.label } as Property);
    }

    // Inject __filter__ synthetics for reference properties with filters
    for (const prop of model.properties) {
      if ((prop.type === 'reference' || (prop.type === 'object' && prop.reference)) && prop.filter) {
        augmentedModel.properties.push({
          name: `__filter__${prop.name}`,
          type: 'object',
          computed: prop.filter,
        } as Property);
      }
    }

    // Inject __action__ synthetics for action hidden/disabled
    for (const action of model.actions) {
      if (action.hidden || action.disabled) {
        augmentedModel.properties.push({
          name: `__action__${action.name}`,
          type: 'boolean',
          hidden: action.hidden,
          disabled: action.disabled,
        } as Property);
      }
    }

    const propertyMap = new Map(augmentedModel.properties.map(p => [p.name, p]));
    const dependencies = this.parseDependencies(augmentedModel);

    // Track which properties need Component[][] conversion after initialization
    const referenceProps: { propName: string; nestedModel: Model; entities: Entity[] }[] = [];

    // Phase 1: Create components with Entity values (labels evaluate against plain objects)
    const components: Component[] = augmentedModel.properties.map((prop) => {
      const existingValue = (entityData as Record<string, unknown> | undefined)?.[prop.name];

      const nestedModel = prop.type === 'object'
        ? (prop.reference ?? { name: prop.name, properties: prop.properties, actions: [] } as unknown as Model)
        : prop.type === 'reference'
        ? prop.reference
        : undefined;

      if (nestedModel && nestedModel.properties) {
        if (nestedModel.id && visited.has(nestedModel.id)) {
          return {
            name: prop.name,
            description: prop.description,
            state: [[], (v: unknown) => this.cascadeAndNotify(state, index, prop.name, v, dependencies, propertyMap)],
            placeholder: prop.name,
            readonly: !!prop.computed,
          } as Component;
        }

        const entities = (Array.isArray(existingValue) ? existingValue : existingValue ? [existingValue] : []) as Entity[];
        referenceProps.push({ propName: prop.name, nestedModel, entities });

        return {
          name: prop.name,
          description: prop.description,
          state: [existingValue, (v: unknown) => this.cascadeAndNotify(state, index, prop.name, v, dependencies, propertyMap)],
          placeholder: prop.name,
          readonly: !!prop.computed,
        } as Component;
      }

      if (prop.type === 'custom') {
        return {
          name: prop.name,
          description: prop.description,
          state: [prop.code ?? '', (v: unknown) => this.cascadeAndNotify(state, index, prop.name, v, dependencies, propertyMap)],
          placeholder: prop.name,
        } as Component;
      }

      const defaultValue = prop.array ? []
        : prop.type === 'boolean' ? false
        : prop.type === 'integer' ? 0
        : '';
      const value = existingValue ?? defaultValue;

      return {
        name: prop.name,
        description: prop.description,
        state: [value, (v: unknown) => this.cascadeAndNotify(state, index, prop.name, v, dependencies, propertyMap)],
        placeholder: prop.name,
        readonly: !!prop.computed,
      } as Component;
    });

    // Phase 2: Initialize (evaluates computed values and labels against Entity values)
    // Entity is "new" if null, or if it only contains 'id' and metadata fields (_createdAt, etc.).
    // The latter case handles staged new entities: Page effect 2b creates an empty staging record
    // with just an id, then database.get() returns it here — we still need defaults to evaluate.
    const isNew = entityData === null || Object.keys(entityData).every(k => k === 'id' || k.startsWith('_'));
    const initialized = this.initialize(components, propertyMap, isNew);
    for (let i = 0; i < initialized.length; i++) {
      components[i] = initialized[i];
    }

    // Phase 3: Convert reference properties from Entity to Component[][]
    for (const { propName, nestedModel, entities: phase1Entities } of referenceProps) {
      const compIndex = components.findIndex(c => c.name === propName);
      if (compIndex === -1) continue;

      const comp = components[compIndex];
      const currentValue = comp.state![0];

      // Re-derive entities: use post-Phase-2 value if it changed (e.g., from default evaluation)
      // Entity objects have 'id' but not 'state' (which Component objects have)
      let entities = phase1Entities;
      if (currentValue && typeof currentValue === 'object' && !Array.isArray(currentValue)
          && 'id' in currentValue && !('state' in currentValue)) {
        entities = [currentValue as Entity];
      } else if (Array.isArray(currentValue) && currentValue.length > 0
          && typeof currentValue[0] === 'object' && currentValue[0] !== null
          && 'id' in currentValue[0] && !('state' in currentValue[0])
          && !Array.isArray(currentValue[0])) {
        entities = currentValue as Entity[];
      }

      const nestedReactiveEntities = entities.map((entity, nestedIndex) =>
        this.createReactiveEntity(nestedModel, entity, nestedIndex, state, visited)
      );

      const nestedComponents = nestedReactiveEntities
        .filter(re => re.entity)
        .map(re => [
          { name: 'id', state: [re.entity!.id, () => {}] } as Component,
          ...re.components
        ]);

      components[compIndex] = {
        ...comp,
        state: [nestedComponents, comp.state![1]],
      };
    }

    // Sync entity with live component values (post-Phase-2 defaults/computed + Phase-3 reference IDs)
    const entity = entityData ? this.buildEntityData(components, propertyMap, entityData.id) : null;
    return { entity, components };
  }

  /**
   * Called by component setters. Updates value, cascades to dependents,
   * and notifies via callback. Operates on the sustain-scoped state.
   */
  private cascadeAndNotify(
    state: SustainState,
    entityIndex: number,
    propName: string,
    newValue: unknown,
    dependencies: Map<string, Set<string>>,
    propertyMap: Map<string, Property>
  ): void {
    const re = state.reactiveEntities[entityIndex];
    if (!re) return;

    // Get dependents that need recalculation
    const toRecalculate = new Set<string>();
    const queue = [propName];
    while (queue.length > 0) {
      const current = queue.shift()!;
      const dependents = dependencies.get(current);
      if (dependents) {
        for (const dep of dependents) {
          if (!toRecalculate.has(dep)) {
            toRecalculate.add(dep);
            queue.push(dep);
          }
        }
      }
    }

    const components = [...re.components];
    const indexMap = new Map(components.map((c, i) => [c.name, i]));

    // Update the changed component
    const changedIndex = indexMap.get(propName);
    if (changedIndex !== undefined) {
      components[changedIndex] = { ...components[changedIndex], state: [newValue, components[changedIndex].state![1]] };
    }

    // Recalculate dependents if any
    if (toRecalculate.size > 0) {
      const context = this.buildContext(components);

      for (const name of toRecalculate) {
        const idx = indexMap.get(name);
        if (idx === undefined) continue;

        const c = components[idx];
        const prop = propertyMap.get(name);
        if (!prop) continue;

        const updated = { ...c };

        if (prop.computed) {
          const computedValue = this.evaluate(prop.computed, context);
          if (computedValue !== undefined) {
            const stateValue = prop.type === 'object' ? computedValue : String(computedValue);
            updated.state = [stateValue, c.state![1]] as [unknown, (value: unknown) => void];
            context[name] = computedValue;
          }
        }

        if (prop.label) {
          updated.label = String(this.evaluate(prop.label, context) ?? '');
        }
        if (prop.hidden) {
          updated.hidden = Boolean(this.evaluate(prop.hidden, context));
        }
        if (prop.readonly) {
          updated.readonly = Boolean(this.evaluate(prop.readonly, context));
        }
        if (prop.disabled) {
          updated.disabled = Boolean(this.evaluate(prop.disabled, context));
        }
        if (prop.required) {
          updated.required = Boolean(this.evaluate(prop.required, context));
        }

        components[idx] = updated;
      }
    }

    // Update the reactive entity (including synced entity data) and notify
    state.reactiveEntities = [...state.reactiveEntities];
    const updatedEntity = re.entity ? this.buildEntityData(components, propertyMap, re.entity.id) : null;
    state.reactiveEntities[entityIndex] = { ...re, entity: updatedEntity, components };
    state.callback(state.reactiveEntities);
  }

  private parseDependencies(model: Model): Map<string, Set<string>> {
    const dependencies = new Map<string, Set<string>>();

    for (const prop of model.properties) {
      const codeStrings = [
        prop.computed,
        prop.label,
        prop.readonly,
        prop.disabled,
        prop.hidden,
        prop.required
      ].filter(Boolean);

      for (const code of codeStrings) {
        const deps = this.extractDependencies(code!);
        for (const dep of deps) {
          if (!dependencies.has(dep)) {
            dependencies.set(dep, new Set());
          }
          dependencies.get(dep)!.add(prop.name);
        }
      }
    }

    return dependencies;
  }

  private extractDependencies(code: string): string[] {
    const deps: string[] = [];
    const regex = /(?:this|entity)\.(\w+)/g;
    let match;
    while ((match = regex.exec(code)) !== null) {
      deps.push(match[1]);
    }
    return deps;
  }

  private evaluate(code: string, context: Record<string, unknown>): unknown {
    return this.runner.evaluate(code, context);
  }

  private buildContext(components: Component[]): Record<string, unknown> {
    const context: Record<string, unknown> = {};
    for (const c of components) {
      if (c.name) {
        const val = c.state?.[0];
        if (Array.isArray(val) && val.length > 0 && Array.isArray(val[0]) &&
            typeof val[0][0] === 'object' && 'state' in (val[0][0] as object)) {
          context[c.name] = this.componentsToObject(val as { name?: string; state?: [unknown, unknown] }[][]);
        } else {
          context[c.name] = val ?? '';
        }
      }
    }
    return context;
  }

  private componentsToObject(rows: { name?: string; state?: [unknown, unknown] }[][]): Record<string, unknown> | Record<string, unknown>[] | null {
    if (!rows || rows.length === 0) return null;

    const rowToObj = (row: { name?: string; state?: [unknown, unknown] }[]): Record<string, unknown> => {
      const obj: Record<string, unknown> = {};
      for (const comp of row) {
        if (comp.name && comp.state) {
          const val = comp.state[0];
          if (Array.isArray(val) && val.length > 0 && Array.isArray(val[0]) &&
              typeof val[0][0] === 'object' && 'state' in (val[0][0] as object)) {
            obj[comp.name] = this.componentsToObject(val as { name?: string; state?: [unknown, unknown] }[][]);
          } else {
            obj[comp.name] = val;
          }
        }
      }
      return obj;
    };

    if (rows.length === 1) {
      return rowToObj(rows[0]);
    }
    return rows.map(rowToObj);
  }

  /**
   * Build a plain entity object from components, suitable for database writes.
   * Extracts entity IDs from Component[][] for reference properties,
   * plain objects for object properties, and raw values for scalars.
   */
  private buildEntityData(components: Component[], propertyMap: Map<string, Property>, id?: string): Entity {
    const entity: Record<string, unknown> = {};
    if (id) entity.id = id;
    for (const c of components) {
      if (!c.name || c.name.startsWith('__') || c.name === 'id') continue;
      const prop = propertyMap.get(c.name);
      if (!prop || prop.type === 'custom') continue;
      const val = c.state?.[0];
      if (val === undefined) continue;
      // Reference / reference-bearing object: Phase 3 stores Component[][], possibly empty.
      // Key off prop.type (not val shape) so an empty selection still produces null / [],
      // instead of leaking the Component[][] placeholder into storage.
      if (prop.type === 'reference' || (prop.type === 'object' && prop.reference)) {
        const rows = (Array.isArray(val) ? val : []) as { name?: string; state?: [unknown, unknown] }[][];
        const ids = rows.map(row => row.find?.(col => col.name === 'id')?.state?.[0]).filter(Boolean);
        entity[c.name] = prop.array ? ids : ids[0] ?? null;
        continue;
      }
      // Plain nested object (no reference): convert Component[][] to plain objects when populated.
      if (Array.isArray(val) && val.length > 0 && Array.isArray(val[0])
          && typeof val[0][0] === 'object' && val[0][0] !== null && 'state' in (val[0][0] as object)) {
        entity[c.name] = this.componentsToObject(val as { name?: string; state?: [unknown, unknown] }[][]);
        continue;
      }
      entity[c.name] = val;
    }
    return entity as Entity;
  }

  private initialize(components: Component[], propertyMap: Map<string, Property>, isNew: boolean): Component[] {
    const context = this.buildContext(components);

    return components.map(c => {
      const prop = propertyMap.get(c.name!);
      if (!prop || !c.name) return c;

      const updated = { ...c };

      if (prop.computed) {
        const computedValue = this.evaluate(prop.computed, context);
        if (computedValue !== undefined) {
          const stateValue = prop.type === 'object' ? computedValue : String(computedValue);
          updated.state = [stateValue, c.state![1]] as [unknown, (value: unknown) => void];
          context[c.name] = computedValue;
        }
      }

      if (isNew && prop.default && !prop.computed) {
        const defaultValue = this.evaluate(prop.default, context);
        if (defaultValue !== undefined) {
          updated.state = [defaultValue, c.state![1]] as [unknown, (value: unknown) => void];
          context[c.name] = defaultValue;
        }
      }

      if (c.label !== undefined) {
        // Keep existing label
      } else if (prop.label) {
        updated.label = String(this.evaluate(prop.label, context) ?? '');
      } else if ((prop.type === 'reference' || (prop.type === 'object' && prop.reference)) && prop.reference?.label) {
        // During initialize(), reference values are still plain entities (pre-Phase-3),
        // so we only need to handle Entity/Entity[] here, not Component[][].
        const refValue = context[c.name];
        if (refValue && typeof refValue === 'object') {
          const enrichRef = (entity: Record<string, unknown>): Record<string, unknown> => {
            const enriched = { ...entity };
            for (const refProp of prop.reference!.properties) {
              if (refProp.computed) {
                enriched[refProp.name] = this.evaluate(refProp.computed, enriched);
              }
            }
            return enriched;
          };
          if (Array.isArray(refValue)) {
            updated.label = refValue
              .map(v => String(this.evaluate(prop.reference!.label!, enrichRef(v as Record<string, unknown>)) ?? ''))
              .join(', ');
          } else {
            updated.label = String(this.evaluate(prop.reference!.label!, enrichRef(refValue as Record<string, unknown>)) ?? '');
          }
        } else {
          updated.label = '';
        }
      }

      if (prop.hidden) {
        updated.hidden = Boolean(this.evaluate(prop.hidden, context));
      }
      if (prop.readonly) {
        updated.readonly = Boolean(this.evaluate(prop.readonly, context));
      }
      if (prop.disabled) {
        updated.disabled = Boolean(this.evaluate(prop.disabled, context));
      }
      if (prop.required) {
        updated.required = Boolean(this.evaluate(prop.required, context));
      }

      return updated;
    });
  }
}
